// src/pages/Homepage.js
import React, { Suspense, useEffect, useState } from 'react';
import './Homepage.css';
import {
  FaPuzzlePiece,
  FaChartLine,
  FaRobot,
  FaMoneyBillWave,
  FaHandshake,
  FaCheckCircle,
  FaUsers,
  FaLink,
  FaBullhorn,
  FaDollarSign
} from 'react-icons/fa';
import { useAuth } from '../components/AuthContext';
import { useNavigate } from 'react-router-dom';
import { trackConnectGroupClick } from '../analytics';

const DynamicChat = React.lazy(() => import('./DynamicChat'));

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

function Homepage() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // helper para navegar e marcar CTA sem duplicar com a heurística do App
  const goLoginTracked = (source) => {
    // marca o evento apenas se não estiver logado
    if (!user) {
      trackConnectGroupClick(source);
      // sinaliza ao AnalyticsHub que este clique já foi marcado manualmente
      window.__ctaManualFired = true;
    }
    navigate('/login');
  };

  const goSmart = (source) => {
    if (user) {
      navigate('/dashboard');
    } else {
      goLoginTracked(source);
    }
  };

  return (
    <div className="homepage">
      <main role="main">
        {/* 1. Seção Hero */}
        <section className="hero" id="hero">
          <div className="container hero-container">
            <div className="hero-text">
              <div className="hero-header">
                <div className="brand">
                  <picture>
                    <source srcSet="/brazfy.avif" type="image/avif" />
                    <source srcSet="/brazfy.webp" type="image/webp" />
                    <img
                      src="/brazfy.png"
                      alt="Logo Brazfy"
                      className="brand-logo"
                      width="205"
                      height="80"
                      decoding="async"
                      fetchpriority="high"
                      loading="eager"
                    />
                  </picture>
                </div>

                <button
                  className="login-btn"
                  onClick={() => goSmart('header_login_btn')}
                >
                  {user ? 'Meu Painel' : 'Entrar agora'}
                </button>
              </div>

              {/* Tagline e Título */}
              <p className="hero-tagline">100% Gratuito • Sem Mensalidades</p>
              <h1>
                Transforme seu grupo do WhatsApp em uma <span>máquina de ganhar dinheiro</span>
              </h1>

              {/* Chat apenas no mobile */}
              {isMobile && (
                <div className="mobile-chat">
                  <Suspense fallback={<div className="chat-skeleton" aria-hidden="true" />}>
                    <DynamicChat />
                  </Suspense>
                </div>
              )}

              <p className="hero-description">
                Adicione nosso feed de promoções ao seu grupo do WhatsApp e comece a receber comissões em cada venda.
              </p>

              <>
                <div className="hero-actions">
                  <button
                    className="cta-primary"
                    onClick={() => goSmart('hero_primary')}
                  >
                    {user ? 'Acessar meu Painel' : 'Conectar meu Grupo (Grátis)'}
                    <span className="cta-arrow">⬅</span>
                  </button>
                  <a
                    href="https://chat.whatsapp.com/CwrhWForancFn1dBeD17cI"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-secondary"
                  >
                    Acessar grupo de exemplo
                  </a>
                </div>
                <p className="hero-sub-cta">
                  Sem cartão • Sem burocracia • Comece em minutos
                </p>
              </>
            </div>

            {/* Chat apenas no desktop */}
            {!isMobile && (
              <div className="hero-image">
                <Suspense fallback={<div className="chat-skeleton" aria-hidden="true" />}>
                  <DynamicChat />
                </Suspense>
              </div>
            )}
          </div>
        </section>

        {/* 2. Seção Sobre (Passo a Passo) */}
        <section className="about" id="about" aria-labelledby="como-funciona-title">
          <div className="container about-container">
            <h2 id="como-funciona-title">Como funciona</h2>
            <div className="steps-container">
              <div className="step-card">
                <FaUsers size={48} color="var(--brand)" aria-hidden="true" focusable="false" />
                <h3>1. Crie ou use seu grupo</h3>
                <p>Utilize um grupo existente ou crie um novo grupo no WhatsApp.</p>
              </div>
              <div className="step-card">
                <FaLink size={48} color="var(--brand)" aria-hidden="true" focusable="false" />
                <h3>2. Conecte nosso feed</h3>
                <p>Adicione o feed de promoções do Brazfy ao seu grupo de forma simples e rápida.</p>
              </div>
              <div className="step-card">
                <FaBullhorn size={48} color="var(--brand)" aria-hidden="true" focusable="false" />
                <h3>3. Divulgue seu grupo</h3>
                <p>Convide pessoas para aumentar o alcance e as chances de vendas.</p>
              </div>
              <div className="step-card">
                <FaDollarSign size={48} color="var(--brand)" aria-hidden="true" focusable="false" />
                <h3>4. Receba comissões</h3>
                <p>Cada venda realizada gera comissões direto para você, sem burocracia!</p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Seção Recursos */}
        <section className="features" id="features" aria-labelledby="recursos-title">
          <div className="container features-container">
            <div className="feature-item">
              <FaPuzzlePiece size={64} color="var(--brand)" aria-hidden="true" focusable="false" />
              <h3>Integração fácil</h3>
              <p>
                Adicione nosso feed ao seu grupo do WhatsApp sem complicações e comece a monetizar imediatamente.
              </p>
            </div>
            <div className="feature-item">
              <FaChartLine size={64} color="var(--brand)" aria-hidden="true" focusable="false" />
              <h3>Rastreamento preciso</h3>
              <p>
                Acompanhe cliques e conversões com relatórios detalhados para maximizar suas comissões.
              </p>
            </div>
            <div className="feature-item">
              <FaRobot size={64} color="var(--brand)" aria-hidden="true" focusable="false" />
              <h3>Automatização de promoções</h3>
              <p>
                Programe seu feed de promoções e deixe a Brazfy cuidar de tudo para você.
              </p>
            </div>
          </div>
        </section>

        {/* 4. Seção Preço */}
        <section className="pricing" id="pricing" aria-labelledby="preco-title">
          <div className="container pricing-container">
            <h2 id="preco-title">Sem custos para você</h2>
            <div className="pricing-cards">
              <div className="pricing-card">
                <FaMoneyBillWave size={64} color="var(--brand)" aria-hidden="true" focusable="false" />
                <h3>Gratuito</h3>
                <p>
                  Utilize Brazfy sem mensalidades ou taxas. É 100% grátis para você!
                </p>
              </div>
              <div className="pricing-card">
                <FaHandshake size={64} color="var(--brand)" aria-hidden="true" focusable="false" />
                <h3>Comissões sobre vendas</h3>
                <p>
                  Nós ganhamos comissões apenas sobre as vendas realizadas. Você não paga nada.
                </p>
              </div>
              <div className="pricing-card">
                <FaCheckCircle size={64} color="var(--brand)" aria-hidden="true" focusable="false" />
                <h3>Transparência total</h3>
                <p>
                  Sem custos ocultos – você tem controle total e transparência em todos os processos.
                </p>
              </div>
            </div>
            <div className="pricing-cta">
              <button
                className="cta-primary"
                onClick={() => goSmart('pricing_cta')}
              >
                Cadastre-se gratuitamente
              </button>
            </div>
          </div>
        </section>

        {/* 5. Seção Contato */}
        <section className="contact" id="contact" aria-labelledby="contato-title">
          <div className="container contact-container">
            <div className="contact-card">
              <h2 id="contato-title">Entre em contato</h2>
              <p>
                Quer saber mais? Clique no botão abaixo e fale conosco para descobrir como o Brazfy pode transformar o seu grupo.
              </p>
              <a
                href="https://wa.me/5511934459150"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-button"
                aria-label="Abrir conversa de WhatsApp com a equipe Brazfy"
              >
                Fale conosco
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="homepage-footer" role="contentinfo">
        <div className="container footer-container">
          <p>&copy; 2025 Brazfy. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}

export default Homepage;